import React, { useEffect, useState } from "react";
import { FileText, Link2, X, Download } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function AdminPolicies() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [docOpen, setDocOpen] = useState(null);
  const [docUrl, setDocUrl] = useState("");
  const [docName, setDocName] = useState("");

  const load = async () => {
    const r = await api.get("/admin/policies");
    setList(r.data);
    const c = await api.get("/admin/customers");
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const custName = (id) => customers.find((c) => c.id === id)?.full_name || "—";

  const uploadDoc = async () => {
    if (!docUrl) return toast.error("Provide a document URL");
    try {
      await api.patch(`/admin/policies/${docOpen.id}/document`, {
        document_url: docUrl, document_name: docName || "Policy Document",
      });
      toast.success("Policy document linked");
      setDocOpen(null); setDocUrl(""); setDocName(""); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-policies-page">
      <h1 className="text-3xl font-extrabold tracking-tight">Policies</h1>
      <p className="text-sm text-slate-500">{list.length} active policies · Link policy documents</p>

      <div className="mt-5 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Policy</th>
              <th className="text-left p-3">Customer</th>
              <th className="text-right p-3">Premium</th>
              <th className="text-right p-3">Collected</th>
              <th className="text-right p-3">Due</th>
              <th className="text-center p-3">Document</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((p) => (
              <tr key={p.id} data-testid={`pol-row-${p.id}`}>
                <td className="p-3">
                  <div className="font-bold">{p.policy_number}</div>
                  <div className="text-[11px] text-slate-400">{p.policy_name} · {p.frequency}</div>
                </td>
                <td className="p-3 text-slate-600">{custName(p.customer_id)}</td>
                <td className="p-3 text-right font-extrabold">{fmtINR(p.premium_amount)}</td>
                <td className="p-3 text-right text-emerald-600">{fmtINR(p.collected_amount || 0)}</td>
                <td className="p-3 text-right text-slate-500 text-xs">{fmtDate(p.due_date)}</td>
                <td className="p-3 text-center">
                  {p.document_url ? (
                    <a href={p.document_url} target="_blank" rel="noreferrer" data-testid={`view-doc-${p.id}`} className="text-emerald-600 inline-flex items-center gap-1 text-xs font-bold">
                      <Download className="w-3 h-3" /> Linked
                    </a>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">none</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`set-doc-${p.id}`} onClick={() => { setDocOpen(p); setDocUrl(p.document_url || ""); setDocName(p.document_name || ""); }} className="text-[11px] font-bold text-tenant-primary inline-flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> {p.document_url ? "Edit" : "Add"}
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No policies yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {docOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setDocOpen(null)}>
          <div className="bg-white rounded-3xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <div className="font-extrabold text-lg flex items-center gap-2"><FileText className="w-5 h-5" /> Link Policy Document</div>
              <button onClick={() => setDocOpen(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mb-3">For: <b>{docOpen.policy_number}</b></div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Document Name</label>
            <input data-testid="doc-name" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="e.g. LIC Policy Bond" className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mt-3 block">Document URL</label>
            <input data-testid="doc-url" value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="https://drive.google.com/..." className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
            <div className="text-[11px] text-slate-400 mt-1">Paste a Google Drive/Dropbox/S3 link to the policy PDF</div>
            <button data-testid="save-doc-url" onClick={uploadDoc} className="w-full mt-4 bg-tenant-primary text-white py-3 rounded-xl font-bold">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
